import type { FC } from 'react';
import React, { Fragment } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import Avatar from '@atlaskit/avatar';
import { DynamicTableStateless } from '@atlaskit/dynamic-table';
import type { RowType, RowCellType } from '@atlaskit/dynamic-table/types';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import { ModalBody } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';

import { ObservableSpinner } from '@confluence/infinite-scroll';
import { ExternalCollaboratorUserLozenge } from '@confluence/external-collab-ui';

import { PRINCIPAL_TYPE } from '../constants';

import { GroupCountComponent } from './GroupCountComponent';
import { SitePermissionType as ContentAccessSitePermissionType } from './__types__/AccessDetailsDialogContentAccessDetailsQuery';
import type { AccessDetailsDialogContentAccessDetailsQuery as AccessDetailsDialogContentAccessDetailsQuery$data } from './__types__/AccessDetailsDialogContentAccessDetailsQuery';
import type { AccessDetailsDialogSpaceAccessDetailsQuery as AccessDetailsDialogSpaceAccessDetailsQuery$data } from './__types__/AccessDetailsDialogSpaceAccessDetailsQuery';
import {
	PrincipalType as SpaceAccessPrincipalType,
	SitePermissionType as SpaceAccessSitePermissionType,
} from './__types__/AccessDetailsDialogSpaceAccessDetailsQuery';

type AccessDetailsDialogBodyProps = {
	filterText: string;
	spaceInfo?: AccessDetailsDialogSpaceAccessDetailsQuery$data;
	contentInfo?: AccessDetailsDialogContentAccessDetailsQuery$data;
	isLoading: boolean;
	hasInheritedRestrictions: boolean;
	handleSpaceAccessDetailsFetchMore: (_?: string | null) => void;
	handleContentAccessDetailsFetchMore: (_?: string | null) => void;
};

type AccessSubject = {
	id?: string | null;
	type: PRINCIPAL_TYPE;
	isGuest: boolean;
	displayName?: string | null;
	secondLine?: string | null;
	avatarUrl?: string;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	table: {
		border: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'thead tr :nth-child(1)': {
			border: 'none',
			width: '36px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		th: {
			border: 'none',
			paddingTop: token('space.0'),
			paddingBottom: token('space.0'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		tbody: {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'tr:nth-of-type(1)': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				td: {
					paddingTop: token('space.0'),
				},
			},
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PrincipalName = styled.span({
	color: token('color.text'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExternalCollaboratorUserLozengeWrapper = styled.span({
	marginLeft: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Email = styled.span({
	color: token('color.text.subtlest'),
});

// matching Avatar: 32 - size of the medium icon + the icon component ads wrapper with 2px margin
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PeopleGroupIconWrapper = styled.div({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	borderRadius: '50%',
	backgroundColor: token('color.background.accent.gray.subtler'),
	width: '32px',
	height: '32px',
	margin: token('space.025'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableHeaderSpan = styled.span({
	border: 0,
	clip: 'rect(1px, 1px, 1px, 1px)',
	clipPath: 'inset(50%)',
	height: '1px',
	margin: token('space.0'),
	overflow: 'hidden',
	padding: token('space.0'),
	position: 'absolute',
	width: '1px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	wordWrap: 'normal !important',
});

export const ACCESS_DETAILS_TABLE_HEAD = {
	cells: [
		{
			key: 'avatar',
			content: '',
		},
		{
			key: 'principal',
			content: (
				<FormattedMessage
					id="space-access-details.access-details-table.header.principal"
					defaultMessage="User/group"
					description="Header text of restrictions dialog column, whose rows contain a user or a group"
				>
					{(tableHeaderText) => <TableHeaderSpan>{tableHeaderText}</TableHeaderSpan>}
				</FormattedMessage>
			),
		},
	],
};

function getTableRowCells({
	type,
	id,
	displayName,
	secondLine,
	avatarUrl,
	isGuest,
	filterText,
}: AccessSubject & {
	filterText: string;
}): { key: string; cells: RowCellType[] } {
	const tablePrincipalAvatar =
		type === PRINCIPAL_TYPE.USER ? (
			<Avatar src={avatarUrl} appearance="circle" size="medium" label="" />
		) : (
			<PeopleGroupIconWrapper>
				<PeopleGroupIcon label="" spacing="spacious" color={token('color.icon')} />
			</PeopleGroupIconWrapper>
		);

	const groupId = type === PRINCIPAL_TYPE.GROUP ? id : null;

	return {
		key: `${type}-${id}`,
		cells: [
			{
				key: 'avatar',
				content: tablePrincipalAvatar,
			},
			{
				key: 'principal',
				content: (
					<Fragment>
						<PrincipalWithSearchHighlight
							displayName={displayName || ''}
							filterText={filterText}
							email={secondLine || ''}
							isGuest={isGuest}
						/>
						{Boolean(groupId) && <GroupCountComponent groupId={groupId} />}
					</Fragment>
				),
			},
		],
	};
}

function formatSubjects({
	subjectList,
	filterText,
}: {
	subjectList: AccessSubject[];
	filterText: string;
}): RowType[] {
	if (!subjectList) {
		return [];
	}
	return subjectList.map((item) =>
		getTableRowCells({
			...item,
			filterText,
		}),
	);
}

const PrincipalWithSearchHighlight: FC<{
	displayName: string;
	isGuest: boolean;
	email?: string;
	filterText: string;
}> = ({ displayName, email, isGuest }) => {
	return (
		<Fragment>
			<div>
				{/*
          Removing bold highlighting of search match sections due to:
          https://asecurityteam.atlassian.net/browse/VULN-1084090

          Ticket for bringing that feature back:
          https://product-fabric.atlassian.net/browse/CPE-1437
        */}
				<PrincipalName>{displayName}</PrincipalName>
				{isGuest && (
					<ExternalCollaboratorUserLozengeWrapper>
						<ExternalCollaboratorUserLozenge />
					</ExternalCollaboratorUserLozengeWrapper>
				)}
			</div>
			{Boolean(email) && <Email>{email}</Email>}
		</Fragment>
	);
};

function parseSpaceInfoSubjects(
	spaceInfo?: AccessDetailsDialogSpaceAccessDetailsQuery$data,
): AccessSubject[] {
	return (spaceInfo?.spacePermissions?.subjectsWithPermissions?.nodes || []).map((item) => {
		if (item?.subjectKey?.principalType === SpaceAccessPrincipalType.USER) {
			return {
				id: item?.subjectKey?.id,
				type: PRINCIPAL_TYPE.USER,
				displayName: item?.subjectKey?.displayName,
				avatarUrl: item?.subjectKey?.user?.profilePicture?.path || '',
				secondLine: item?.subjectKey?.user?.email,
				isGuest: item?.subjectKey?.user?.permissionType === SpaceAccessSitePermissionType.EXTERNAL,
			};
		}

		// else not a user, so it must be a group
		return {
			id: item?.subjectKey?.id,
			type: PRINCIPAL_TYPE.GROUP,
			displayName: item?.subjectKey?.displayName,
			isGuest: item?.subjectKey?.group?.permissionType === SpaceAccessSitePermissionType.EXTERNAL,
		};
	});
}

function parseContentInfoSubjects(
	contentInfo?: AccessDetailsDialogContentAccessDetailsQuery$data,
): AccessSubject[] {
	return (
		contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.nodes || []
	).map((item) => {
		if (item?.type === PRINCIPAL_TYPE.USER) {
			return {
				id: item?.id,
				type: PRINCIPAL_TYPE.USER,
				displayName: item?.displayName,
				avatarUrl: item?.user?.profilePicture?.path || '',
				secondLine: item?.user?.email,
				isGuest: item?.user?.permissionType === ContentAccessSitePermissionType.EXTERNAL,
			};
		}

		// else not a user, so it must be a group
		return {
			id: item?.id,
			type: PRINCIPAL_TYPE.GROUP,
			displayName: item?.displayName,
			isGuest: item?.group?.permissionType === ContentAccessSitePermissionType.EXTERNAL,
		};
	});
}

export const AccessDetailsDialogBody: FC<AccessDetailsDialogBodyProps> = ({
	filterText,
	spaceInfo,
	contentInfo,
	hasInheritedRestrictions,
	handleContentAccessDetailsFetchMore,
	handleSpaceAccessDetailsFetchMore,
	isLoading,
}) => {
	const subjectList = hasInheritedRestrictions
		? parseContentInfoSubjects(contentInfo)
		: parseSpaceInfoSubjects(spaceInfo);

	const hasNextPage = hasInheritedRestrictions
		? Boolean(
				contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.pageInfo
					?.hasNextPage,
			)
		: Boolean(spaceInfo?.spacePermissions?.subjectsWithPermissions?.pageInfo?.hasNextPage);

	const nextPageCursor = hasInheritedRestrictions
		? contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.pageInfo?.endCursor
		: spaceInfo?.spacePermissions?.subjectsWithPermissions?.pageInfo?.endCursor;

	const handleFetchMore = hasInheritedRestrictions
		? handleContentAccessDetailsFetchMore
		: handleSpaceAccessDetailsFetchMore;

	return (
		<ModalBody>
			<TableContainer>
				<DynamicTableStateless
					head={ACCESS_DETAILS_TABLE_HEAD}
					rows={formatSubjects({ subjectList, filterText })}
					isFixedSize
				/>
				{!isLoading && hasNextPage && (
					<ObservableSpinner
						onScrollTo={() => {
							handleFetchMore(nextPageCursor);
						}}
						cursor={nextPageCursor}
						testId="access-details-spinner"
						observerOptions={{
							root: null,
							rootMargin: '0px',
							threshold: 0.05,
						}}
					/>
				)}
			</TableContainer>
		</ModalBody>
	);
};
